import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { flushSync } from 'react-dom'

export type ThemeId =
  | 'warm'
  | 'cool'
  | 'dark'
  | 'glass'
  | 'cartoon'
  | 'illustration'
  | 'pixel'
  | 'geek'

export type ThemeMeta = {
  id: ThemeId
  label: string
  /** 设置页等处展示的完整说明，与图标简称区分 */
  fullName: string
}

const STORAGE_KEY = 'glyphicai-theme'

type DocumentWithViewTransition = Document & {
  startViewTransition?: (update: () => void) => { finished: Promise<void> }
}

const THEMES: ThemeMeta[] = [
  {
    id: 'warm',
    label: '暖色',
    fullName: '灰米色纸锈褐主色偏纸媒阅读',
  },
  {
    id: 'cool',
    label: '云蓝',
    fullName: '青白银灰雾亮青高光与登录底图同谱',
  },
  {
    id: 'dark',
    label: '暗夜',
    fullName: '暗色工作台蓝灰容器蓝系主色选项',
  },
  {
    id: 'glass',
    label: '玻璃',
    fullName: '暗底磨砂电青霓虹偏深空座舱界面',
  },
  {
    id: 'cartoon',
    label: '卡通',
    fullName: '奶油底高饱和橙咖粗边硬偏移阴影',
  },
  {
    id: 'illustration',
    label: '插画',
    fullName: '暖白纸本陶土红赭石软线水彩晕染',
  },
  {
    id: 'pixel',
    label: '像素',
    fullName: '深蓝底琥珀金 UI 薄荷直角硬像素阴影',
  },
  {
    id: 'geek',
    label: '极客',
    fullName: '近黑底收敛绿荧光等宽终端气质',
  },
]

const DARK_THEMES: ThemeId[] = ['dark', 'glass', 'pixel', 'geek']

function readStoredTheme(): ThemeId {
  try {
    const v = localStorage.getItem(STORAGE_KEY) as ThemeId | null
    if (v && THEMES.some((t) => t.id === v)) return v as ThemeId
  } catch {
    /* ignore */
  }
  return 'warm'
}

type ThemeContextValue = {
  theme: ThemeId
  setTheme: (t: ThemeId) => void
  themeOptions: ThemeMeta[]
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(readStoredTheme)

  const setTheme = useCallback((t: ThemeId) => {
    if (t === theme) return

    const doc = document as DocumentWithViewTransition
    if (typeof doc.startViewTransition === 'function') {
      const vt = doc.startViewTransition(() => {
        flushSync(() => {
          setThemeState(t)
        })
      })
      void vt.finished.catch(() => {
        /* ignore transition runtime errors */
      })
    } else {
      setThemeState(t)
    }

    try {
      localStorage.setItem(STORAGE_KEY, t)
    } catch {
      /* ignore */
    }
  }, [theme])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    document.documentElement.style.colorScheme = DARK_THEMES.includes(theme)
      ? 'dark'
      : 'light'
  }, [theme])

  const value = useMemo(
    () => ({ theme, setTheme, themeOptions: THEMES }),
    [theme, setTheme],
  )

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}

export { THEMES }
